import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCCheckBox, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRefModalProps, RCCRefSpreadsheetProps, RCCServicesApi, RCCSwitch, RCCTooltip, RCCSelectOptionsCard } from "react-common-components";
import { useForm } from "react-hook-form";
import Audit from "src/components/Audit";
import Checklists from "src/components/Checklists";
import DateTimePicker from "src/components/DateTimePicker";
import InputNumber from "src/components/InputNumber";
import LovTags from "src/components/Lov/Tags";
import LovUsers from "src/components/Lov/Users";
import LovUsersGroups from "src/components/Lov/UsersGroups";
import Modal from "src/components/Modal";
import { ITypeModal } from "src/components/Modal/interface";
import RACI from "src/components/RACI";
import Select from "src/components/Select";

import Spreadsheet from "src/components/Spreadsheet";
import Tabs from "src/components/Tabs";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import convertBooleanValueToCharInArray from "src/utils/convertBooleanValueToCharInArray";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import formatDate, { formatDateWithoutHours } from "src/utils/formatDate";
import stringToNumber from "src/utils/stringToNumber";
import { IComponentActionsPanelModalAddChangeDelete, IRefActionsPanelModalAddChangeDelete, IAction, TabsActionMODAL, ActionSituation, ActionInformValues, ITablesRelated, RecurrenceType, IDateInitial, IListActions, IActionShowPostponementRef } from "../interface";
import addOrChangeSchema from "../schemas";
import BadgeTraceabilityBasic from "./BadgeTraceability/badgeTraceabilityBasic";
import FormSimplifiedFields from "./FormSimplifiedFields";
import CommentsModal, { IActionCommentsRef } from "./modalComments";
import ModalEfficiency, { IActionEfficiencyRef } from "./modalEfficiency";
import ModalHistory, { IActionHistoryRef } from "./modalHistory";
import ModalShowPostponements from "./modalShowPostponements";

const ModalAddChangeDelete: React.ForwardRefRenderFunction<IRefActionsPanelModalAddChangeDelete, IComponentActionsPanelModalAddChangeDelete> = ({
	onCallbackDelete
}, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const { confirm } = useConfirmModal();
	const { user } = useAuth();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();

	const modalCommentsRef = useRef<IActionCommentsRef>(null);
	const modalHistoryRef = useRef<IActionHistoryRef>(null);
	const modalEfficiencyRef = useRef<IActionEfficiencyRef>(null);
	const modalShowPostponements = useRef<IActionShowPostponementRef>(null);
	
	const [selectedAction, setSelectedAction] = useState<IAction>({} as IAction);
	const [actionNotStartedOrDraft, setActionNotStartedOrDraft] = useState<boolean>(false);

	useEffect(() => {
		setActionNotStartedOrDraft(selectedAction.inb001Situacao?.toString() === ActionSituation.NOT_STARTED || selectedAction.inb001Situacao?.toString() === ActionSituation.DRAFT);
	}, [selectedAction]);

	let timer = 0;

	const optionsInformValues = [{
		label: translate("Totais"),
		value: ActionInformValues.TOTAL
	},
	{
		label: translate("Mensais"),
		value: ActionInformValues.MONTHLY
	}];

	// Modal Delete
	const actionButtonDelete = (row: IListActions) => {
		confirm(
			translate("Exclusão da ação"), 
			`${translate("Deseja realmente excluir a ação")}: ${row.inb001Codigo} - ${row.inb001Titulo}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Actions/Delete",
					urlParameters: {
						id: row.inb001Id,
						actionName: row.inb001Titulo
					}
				});
	
				addToast({
					color: "green",
					description: translate("Ação excluída com sucesso"),
					title: translate("Sucesso")
				});
	
				if(onCallbackDelete)
					onCallbackDelete(row.inb001Id, row.situation, row.inb001PercConclusao);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir ação"), addToast);
			},
			true
		);
	};

	// Modal Add/Change
	const [formSimplified, setFormSimplified] = useState(true);
	const [isDisabled, setIsDisabled] = useState(false);
	const [isAdd, setIsAdd] = useState(false);
	const [isCopy, setIsCopy] = useState(false);
	const [title, setTitle] = useState<string>("");
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const [tabInit, setTabInit] = useState(0);
	const [showValuesMonthly, setShowValuesMonthly] = useState(false);
	const [valuesMonthlyData, setValuesMonthlyData] = useState<{ year: string; data: string[][] }[]>([]);
	const [totalsValuesMonthly, setTotalsValuesMonthly] = useState<{ year: string; value: number; realizedValue: number; releasedValue: number; valueRevenue: number; realizedRevenueValue: number ;}[]>([]);
	const [optionsMonthly, setOptionsMonthly] = useState<{value: string; label: string;}[]>([]);
	const [spreadsheetData, setSpreadsheetData] = useState<string[][]>([]);
	const spreadsheetRef = useRef<RCCRefSpreadsheetProps>(null);
	const onCallbackAddOrChange = useRef<Function | undefined>(undefined);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const [parentActionId, setParentActionId] = useState<number | undefined>(undefined);
	const [generateRecurrence, setGenerateRecurrence] = useState(false);
	const [labelRecurrencePeriod, setLabelRecurrencePeriod] = useState("Mensal");
	const [labelNumberRecurrencePeriod, setLabelNumberRecurrencePeriod] = useState("1");
	const [requireEffectiveness, setRequireEffectiveness] = useState(false);

	const { 
		handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, control: controlAddOrChange ,
		reset : resetAddOrChange, setError : setErrorAddOrChange, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange,
		unregister: unregisterAddOrChange, watch: watchAddOrChange, setValue: setValueAddOrChange
	} = useForm();

	const defaultValuesMonthlyData = (defaultPeriodOption: number, defaultData?: {
        year: string;
        data: string[][];
    }[]) => {
		const mountDefaultValuesMonthlyObj = (number: number) => ({
			year: number.toString(),
			data: [
				[`01/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`02/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`03/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`04/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`05/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`06/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`07/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`08/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`09/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`10/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`11/${number.toString()}`, "0", "0", "0", "0", "0"],
				[`12/${number.toString()}`, "0", "0", "0", "0", "0"],
				["Total", "=ROUND(SUM(B1:B12), 2)", "=ROUND(SUM(C1:C12), 2)", "=ROUND(SUM(D1:D12), 2)", "=ROUND(SUM(E1:E12), 2)", "=ROUND(SUM(F1:F12), 2)"]
			]
		});

		if(defaultData){
			const dataX = defaultData.find((data) => data.year === String(defaultPeriodOption))?.data ?? defaultData[0].data;
			setValuesMonthlyData(defaultData);
			setSpreadsheetData(dataX);
			defaultTotalValuesMonthlyData(defaultData);
			return;
		}

		const data = [
			mountDefaultValuesMonthlyObj(defaultPeriodOption)
		];
		setValuesMonthlyData(data);
		setSpreadsheetData(data[0].data);
		defaultTotalValuesMonthlyData(data);
	};

	const defaultTotalValuesMonthlyData = (values: { year: string; data: string[][] }[]) => {
		const updateTotalsValuesMonthly = values.map((valMonthlyDt) => ({
			year: valMonthlyDt.year,
			value: 
					stringToNumber(valMonthlyDt.data[0][1], currentLanguage) + 
					stringToNumber(valMonthlyDt.data[1][1], currentLanguage) + 
					stringToNumber(valMonthlyDt.data[2][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[3][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[4][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[5][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[6][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[7][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[8][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[9][1], currentLanguage) +
					stringToNumber(valMonthlyDt.data[10][1], currentLanguage)+
					stringToNumber(valMonthlyDt.data[11][1], currentLanguage),
			realizedValue:
					stringToNumber(valMonthlyDt.data[0][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[1][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[2][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[3][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[4][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[5][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[6][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[7][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[8][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[9][2], currentLanguage) +
					stringToNumber(valMonthlyDt.data[10][2], currentLanguage)+
					stringToNumber(valMonthlyDt.data[11][2], currentLanguage),
			releasedValue:
					stringToNumber(valMonthlyDt.data[0][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[1][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[2][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[3][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[4][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[5][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[6][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[7][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[8][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[9][3], currentLanguage) +
					stringToNumber(valMonthlyDt.data[10][3], currentLanguage)+
					stringToNumber(valMonthlyDt.data[11][3], currentLanguage),
			valueRevenue:
					stringToNumber(valMonthlyDt.data[0][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[1][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[2][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[3][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[4][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[5][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[6][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[7][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[8][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[9][4], currentLanguage) +
					stringToNumber(valMonthlyDt.data[10][4], currentLanguage)+
					stringToNumber(valMonthlyDt.data[11][4], currentLanguage), 
			realizedRevenueValue:
					stringToNumber(valMonthlyDt.data[0][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[1][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[2][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[3][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[4][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[5][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[6][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[7][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[8][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[9][5], currentLanguage) +
					stringToNumber(valMonthlyDt.data[10][5], currentLanguage)+
					stringToNumber(valMonthlyDt.data[11][5], currentLanguage)
		}));
		setTotalsValuesMonthly(updateTotalsValuesMonthly);
	};

	const defaultOptionsMonthly = (defaultPeriodOption: number) => {
		const mountDefaultOptionsMonthlyObj = (number: number) => ({
			value: number.toString(),
			label: number.toString()
		});
		const data = [
			mountDefaultOptionsMonthlyObj(defaultPeriodOption - 1), 
			mountDefaultOptionsMonthlyObj(defaultPeriodOption),
			mountDefaultOptionsMonthlyObj(defaultPeriodOption + 1),
			mountDefaultOptionsMonthlyObj(defaultPeriodOption + 2),
			mountDefaultOptionsMonthlyObj(defaultPeriodOption + 3),
			mountDefaultOptionsMonthlyObj(defaultPeriodOption + 4),
			mountDefaultOptionsMonthlyObj(defaultPeriodOption + 5),
			mountDefaultOptionsMonthlyObj(defaultPeriodOption + 6),
			mountDefaultOptionsMonthlyObj(defaultPeriodOption + 7)
		];
		setOptionsMonthly(data);
	};

	const defaultTablesRelated = (action: IAction, tablesRelated?: ITablesRelated) => {
		const updateAction = action;

		updateAction.inb001Ina001Id = tablesRelated?.iniciativeId || null;
		updateAction.inb001Ina002Id = tablesRelated?.factAndCause || null;
		updateAction.inb001Rib003Id = tablesRelated?.riskAnalysis || null;
		updateAction.inb001Rib004Id = tablesRelated?.riskIncident || null;
		updateAction.inb001Mea003Id = tablesRelated?.objectiveId || null;
		updateAction.inb001Pra003Id = tablesRelated?.projectPhase || null;
		updateAction.inb001Ocb001Id = tablesRelated?.occurrenceId || null;

		return updateAction;
	};

	const openModalAddOrChange = async (id?: number, onCallback?: Function, tabInitSelected?: TabsActionMODAL | undefined, parentAction?: undefined | number, tablesRelated?: ITablesRelated, subtitleModal?: string, dateInitial?: IDateInitial, disabled?: boolean, copy?: boolean) => {	
		resetAddOrChange();
		let defaultPeriodOption = new Date().getFullYear();
		if(id){
			setIsAdd(false);
			setIsCopy(copy || false);
			setFormSimplified(false);
			try{
				setLoading(true);
				const {data} = await RCCServicesApi.getRequest({
					method: "Actions/Select",
					urlParameters: {
						id,
						currentLanguage
					}
				});
				setSelectedAction(data as IAction);		
				defaultPeriodOption = new Date(data.inb001Dtinicio).getFullYear();
				defaultValuesMonthlyData(defaultPeriodOption, data.inb001Informavalores.toString() === ActionInformValues.MONTHLY ? data.intbb013 : undefined);
				setLoading(false);
				setShowValuesMonthly(data.inb001Informavalores.toString() === ActionInformValues.MONTHLY);
				setRequireEffectiveness(data.inb001Exigeeficacia === "S");

				const titleModal = () => {
					if(disabled)
						return  translate("Visualizar ação");
					if(copy)
						return  translate("Copiar ação");
					return  translate("Alterar ação");
				};

				setTitle(titleModal());
				setSubtitle(subtitleModal || `${translate("Ação")}: ${data.inb001Codigo} - ${data.inb001Titulo}`);
				setIsDisabled(copy ? false : disabled || data.inb001Situacao?.toString() === ActionSituation.FINISHED || data.inb001Situacao?.toString() === ActionSituation.CANCELED);
			}catch(err){
				setLoading(false);
				standardErrorMessages(err, translate("Erro ao selecionar ação"), addToast);
			}
		}else{
			setIsAdd(true);
			setFormSimplified(true);
			setIsDisabled(false);
			setGenerateRecurrence(false);
			setRequireEffectiveness(false);
			let action = {} as IAction;
			if(dateInitial !== undefined){
				action.inb001Dtinicio = dateInitial.startDate;
				action.inb001Dtfim = dateInitial.endDate;
			}
			action = defaultTablesRelated(action, tablesRelated);
			setSelectedAction(action);
			defaultValuesMonthlyData(defaultPeriodOption);
			setShowValuesMonthly(false);
			setTitle(translate(parentActionId ? "Adicionar subação" : "Adicionar ação"));
			setSubtitle(subtitleModal);
			setLabelNumberRecurrencePeriod("1");
		}
		defaultOptionsMonthly(defaultPeriodOption);
		setParentActionId(parentAction);
		onCallbackAddOrChange.current = onCallback;
		setTabInit(tabInitSelected || 0);
		modalAddOrChange.current?.openModal();
	};

	const submitConfirm = (data: IAction) => {
		if(isAdd && generateRecurrence){
			confirmRecurrence(data);
			return;
		}
		
		if(!isAdd && !isCopy && !actionNotStartedOrDraft && hasChangeChecklist(data)){
			confirmChecklist(data);
			return;
		}

		if(isCopy && selectedAction.traceability.Origem){
			confirmTraceability(data, false);
			return;
		}

		submitAddOrChange(data, false);
	};

	const confirmRecurrence = (data: IAction) => {
		const dataForm: any = data;

		if(generateRecurrence && dataForm.recurrenceEndDate == null){
			addToast({
				title: translate("Atenção"),
				description: translate("Informe uma data final para a recorrência"),
				color: "yellow"
			});
			return;
		}

		const recurrenceEndDateString = formatDate(new Date(dataForm.recurrenceEndDate), currentLanguage);

		confirm(
			translate("Recorrências"), 
			`${translate("Você está criando ações recorrentes até a data")} ${recurrenceEndDateString}. ${translate("Deseja continuar")}?`,
			() => {
				if(!isAdd && !isCopy && !actionNotStartedOrDraft && hasChangeChecklist(data)){
					confirmChecklist(data);
					return;
				}

				if(isCopy && selectedAction.traceability.Origem){
					confirmTraceability(data, false);
					return;
				}

				submitAddOrChange(data, false);
			},
			() => {},
			false
		);
	}; 

	const confirmChecklist = (data: IAction) => {
		confirm(
			translate("Alteração no checklist"), 
			`${translate("Ao alterar os checklist, os percentuais do andamento da ação e seus respectivos históricos serão recalculados")}. ${translate("Deseja continuar")}?`,
			() => {
				if(isCopy && selectedAction.traceability.Origem){
					confirmTraceability(data, true);
					return;
				}
		
				submitAddOrChange(data, true);
			},
			() => {},
			false
		);
	}; 

	const confirmTraceability = (data: IAction, recalcPerc: boolean) => {
		confirm(
			translate("Manter Rastreabilidade"), 
			`${translate("Deseja copiar a rastreabilidade da ação")}?`,
			() => {
				submitAddOrChange(data, recalcPerc, true);
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao copiar ação"), addToast);
			},
			false,
			() => {
				submitAddOrChange(data, recalcPerc, false);
			}
		);
	};

	const hasChangeChecklist = (data: any) => {

		data.intbb009.map((inb009: any) => {
			const newInb009 = inb009;
			delete newInb009.id;

			newInb009.intbb010.map((inb010:any) => {
				const newInb010 = inb010;
				delete newInb010.id;
				newInb010.inb010Verificado = newInb010.inb010Verificado === true || newInb010.inb010Verificado === "S" ? "S" : "N";
				return newInb010;
			});

			return newInb009;
		});

		return JSON.stringify(data.intbb009) !== JSON.stringify(selectedAction.intbb009);
	};

	const submitAddOrChange = async (data: IAction, recalcPerc: boolean, keepTraceability?: boolean) => {
		let msgToastSuccess = "";
		let msgToastError = "";
		const dataForm: any = data;
		try{

			// para não comparar a hora
			dataForm.inb001Dtinicio = formatDateWithoutHours(data.inb001Dtinicio);
			dataForm.inb001Dtfim = formatDateWithoutHours(data.inb001Dtfim);

			if(data.inb001Tags)
				dataForm.inb001Tags = extractSingleValueFromArrayOfObject("value", data.inb001Tags);
			else
				dataForm.inb001Tags = undefined;

			dataForm.inb001Urgencia = Number.isNaN(Number(data.inb001Urgencia)) ? null : Number(data.inb001Urgencia);
			dataForm.inb001Importancia = Number.isNaN(Number(data.inb001Importancia)) ? null : Number(data.inb001Importancia);

			if(dataForm.inb001Informavalores === null)
				dataForm.inb001Informavalores = selectedAction.inb001Informavalores;
		
			if(!dataForm.inb001Tipo)
				dataForm.inb001Tipo = "1";

			if(data.intbb002 && data.intbb002.length > 0)
				dataForm.intbb002 = dataForm.intbb002.map((inb002: any) => {
					if(typeof inb002.inb002Efa005Id === "object")
						return {
							...inb002,
							inb002Efa005Id: inb002.inb002Efa005Id.value
						};
					if(typeof inb002.inb002Efa004Id === "object")
						return {
							...inb002,
							inb002Efa004Id: inb002.inb002Efa004Id.value
						};
					return inb002;
				});
		
			if(dataForm.inb001Taa001Id === null)
				dataForm.inb001Taa001Id = undefined;
			else{
				const sectorId = typeof data.inb001Taa001Id === "object" ? dataForm.inb001Taa001Id.value : data.inb001Taa001Id;
				dataForm.inb001Taa001Id = sectorId === "" ? undefined : sectorId;
			}		

			if(dataForm.inb001Efa004Id === null)	
				dataForm.inb001Efa004Id = undefined;
			else
				dataForm.inb001Efa004Id = typeof data.inb001Efa004Id === "object" ? dataForm.inb001Efa004Id.value : data.inb001Efa004Id;
				
			dataForm.inb001Exigeeficacia = data.inb001Exigeeficacia ? "S" : "N";

			if(typeof data.inb001Efa005Id === "object")
				dataForm.inb001Efa005Id = dataForm.inb001Efa005Id.value;
			if(typeof data.inb001Efa005Id2 === "object")
				dataForm.inb001Efa005Id2 = dataForm.inb001Efa005Id2.value;

			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();
			
			await addOrChangeSchema(translate).validate({...dataForm }, {
				abortEarly: false
			});

			if(data.intbb002 && data.intbb002.length > 0){
				dataForm.intbb002 = data.intbb002.filter((dt: any) => dt.inb002Efa005Id !== 0);
				dataForm.intbb002 = data.intbb002.filter((dt: any) => dt.inb002Efa004Id !== 0);
				dataForm.intbb002 = convertBooleanValueToCharInArray("inb002Notificaparticipante", data.intbb002);
			}

			if(data.intbb009 && data.intbb009.length > 0){
				dataForm.intbb009 = data.intbb009.map((inb009: any) => {
					const newInb009 = inb009;
	
					newInb009.intbb010?.map((inb010:any) => {
						const newInb010 = inb010;
						newInb010.inb010Verificado = newInb010.inb010Verificado === true || newInb010.inb010Verificado === "S" ? "S" : "N";
						return newInb010;
					});
		
					return newInb009;
				});
			}
			

			if(data.inb001Tipo.toString() === "1"){
				dataForm.inb001Duracao =  null;
			}else{
				const splitDuration = data.inb001Duracao.toString().split(":");
				const hours = splitDuration[0];
				const minutes = splitDuration[1];
				dataForm.inb001Duracao = parseFloat(hours) * 60 + parseFloat(minutes);
			}
			dataForm.inb001Valor = typeof dataForm.inb001Valor === "string" ? stringToNumber(dataForm.inb001Valor, currentLanguage) || 0 : dataForm.inb001Valor;
			dataForm.inb001Vlrliberado = typeof dataForm.inb001Vlrliberado === "string" ? stringToNumber(dataForm.inb001Vlrliberado, currentLanguage) || 0 : dataForm.inb001Vlrliberado;
			dataForm.inb001Vlrprevreceita = typeof dataForm.inb001Vlrprevreceita === "string" ? stringToNumber(dataForm.inb001Vlrprevreceita, currentLanguage) || 0 : dataForm.inb001Vlrprevreceita;
			dataForm.inb001Vlrrealizado = typeof dataForm.inb001Vlrrealizado === "string" ? stringToNumber(dataForm.inb001Vlrrealizado, currentLanguage) || 0 : dataForm.inb001Vlrrealizado;
			dataForm.inb001Vlrrealreceita = typeof dataForm.inb001Vlrrealreceita === "string" ? stringToNumber(dataForm.inb001Vlrrealreceita, currentLanguage) || 0 : dataForm.inb001Vlrrealreceita;

			let response;
			if(isAdd){
				msgToastSuccess=translate("Ação adicionada com sucesso");
				msgToastError=translate("Erro ao adicionar ação");

				if(generateRecurrence && dataForm.recurrenceEndDate === null){
					addToast({
						title: translate("Erro"),
						description: translate("Informe uma data final para a recorrência"),
						color: "yellow"
					});
					modalAddOrChange.current?.resetLoadingButton();
					return;
				}
				const recurrence = generateRecurrence ? {
					recurrenceEndDate: dataForm.recurrenceEndDate,
					recurrencePeriod: dataForm.recurrencePeriod,
					recurrenceType: Number(dataForm.recurrenceType)
				} : null;
				response = await RCCServicesApi.postRequest({
					method: "Actions/Insert",
					body: {
						intbb001003: {
							...dataForm,
							inb001Notificaresponsavel: dataForm.inb001Notificaresponsavel ? "S" : "N",
							inb001Notificaautoridade: dataForm.inb001Notificaautoridade ? "S" : "N",
							intbb013: valuesMonthlyData,
							inb015Inb001Id: parentActionId || null,
							inb001Ina001Id: selectedAction.inb001Ina001Id || null,
							inb001Ina002Id: selectedAction.inb001Ina002Id || null,
							inb001Rib003Id: selectedAction.inb001Rib003Id || null,
							inb001Rib004Id: selectedAction.inb001Rib004Id || null,
							inb001Mea003Id: selectedAction.inb001Mea003Id || null,
							inb001Pra003Id: selectedAction.inb001Pra003Id || null,
							inb001Ocb001Id: selectedAction.inb001Ocb001Id || null
						},
						recurrence,
						currentLanguage,
						recalcPerc
					}
				});
			}else{				
				if(!isCopy){
					msgToastSuccess=translate("Ação alterada com sucesso");
					msgToastError=translate("Erro ao alterar ação");

					await RCCServicesApi.putRequest({
						method: "Actions/Edit",
						body: {
							intbb001003: {
								...dataForm,
								inb001Id: selectedAction.inb001Id,
								inb001Notificaresponsavel: dataForm.inb001Notificaresponsavel ? "S" : "N",
								inb001Notificaautoridade: dataForm.inb001Notificaautoridade ? "S" : "N",
								intbb013: valuesMonthlyData
							},
							currentLanguage,
							recalcPerc
						}
					});
				}

				if(isCopy){
					msgToastSuccess=translate("Ação copiada com sucesso");
					msgToastError=translate("Erro ao copiar ação");
					
					response = await RCCServicesApi.putRequest({
						method: "Actions/Copy",
						body: {
							intbb001003: {
								...dataForm,
								inb001Id: selectedAction.inb001Id,
								inb001Notificaresponsavel: dataForm.inb001Notificaresponsavel ? "S" : "N",
								inb001Notificaautoridade: dataForm.inb001Notificaautoridade ? "S" : "N",
								intbb013: valuesMonthlyData
							},
							currentLanguage,
							keepTraceability,
							recalcPerc
						}
					});
				}
			}

			addToast({
				color: "green",
				description: msgToastSuccess,
				title: translate("Sucesso")
			});
				
			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
				
			if(onCallbackAddOrChange.current){
				onCallbackAddOrChange.current(response?.data);
			}

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, msgToastError, addToast);
		}
	};

	const onChangeInformValues = (value: string, period: string) => {
		if(value === ActionInformValues.MONTHLY){
			setShowValuesMonthly(true);

			const dataX = valuesMonthlyData.find((e) => e.year === period)?.data || valuesMonthlyData[0].data;

			const getValueOfColumn = ( valuesMonthlyDataX: {year: string; data: string[][]}[], position: number) => valuesMonthlyDataX.map((e) => {
				const dataY = e.data?.map((x) => x[position]);
				dataY.pop();
				return dataY.reduce((a, b) => stringToNumber(a) + stringToNumber(b), 0);
			}).reduce((a, b) => stringToNumber(a) + stringToNumber(b), 0);
			
			const inb001Valor = getValueOfColumn(valuesMonthlyData, 1);
			const inb001Vlrrealizado = getValueOfColumn(valuesMonthlyData, 2);
			const inb001Vlrliberado = getValueOfColumn(valuesMonthlyData, 3);
			const inb001Vlrprevreceita = getValueOfColumn(valuesMonthlyData, 4);
			const inb001Vlrrealreceita = getValueOfColumn(valuesMonthlyData, 5);

			setValueAddOrChange("inb001Valor",inb001Valor);
			setValueAddOrChange("inb001Vlrrealizado", inb001Vlrrealizado);
			setValueAddOrChange("inb001Vlrliberado", inb001Vlrliberado);
			setValueAddOrChange("inb001Vlrprevreceita", inb001Vlrprevreceita);
			setValueAddOrChange("inb001Vlrrealreceita", inb001Vlrrealreceita);

			setSpreadsheetData(dataX);		

			return;
		}

		setShowValuesMonthly(false);
	};

	const onChangeSelectPeriodsValuesMonthly = (e: {value: string, label: string}) => {
		const {value} = e;
		const yearValues = valuesMonthlyData.find((valueMonthly) => valueMonthly.year === value);
		if(yearValues){
			const newData = yearValues.data as string[][];
			newData.pop();
			newData.push(["Total", "=ROUND(SUM(B1:B12), 2)", "=ROUND(SUM(C1:C12), 2)", "=ROUND(SUM(D1:D12), 2)", "=ROUND(SUM(E1:E12), 2)", "=ROUND(SUM(F1:F12), 2)"]);
			setSpreadsheetData(newData);
			return;
		}
		const newYearValues = {
			year: value,
			data: [
				[`01/${value}`, "0", "0", "0", "0", "0"],
				[`02/${value}`, "0", "0", "0", "0", "0"],
				[`03/${value}`, "0", "0", "0", "0", "0"],
				[`04/${value}`, "0", "0", "0", "0", "0"],
				[`05/${value}`, "0", "0", "0", "0", "0"],
				[`06/${value}`, "0", "0", "0", "0", "0"],
				[`07/${value}`, "0", "0", "0", "0", "0"],
				[`08/${value}`, "0", "0", "0", "0", "0"],
				[`09/${value}`, "0", "0", "0", "0", "0"],
				[`10/${value}`, "0", "0", "0", "0", "0"],
				[`11/${value}`, "0", "0", "0", "0", "0"],
				[`12/${value}`, "0", "0", "0", "0", "0"],
				["Total", "=ROUND(SUM(B1:B12), 2)", "=ROUND(SUM(C1:C12), 2)", "=ROUND(SUM(D1:D12), 2)", "=ROUND(SUM(E1:E12), 2)", "=ROUND(SUM(F1:F12), 2)"]
			]
		};
		setValuesMonthlyData((prevState) => [...prevState, newYearValues]);
		setSpreadsheetData(newYearValues.data);
	};

	const onChangeSpreadsheet = () => {
		clearTimeout(timer);
		timer = window.setTimeout(() => {
			onSaveSpreadsheet();
		}, 1500);
	};

	const onSaveSpreadsheet = () => {
		if(!spreadsheetRef.current)
			return;	
			
		const selectedYearOption = watchAddOrChange("periodsMonthly");
		const index = valuesMonthlyData.findIndex((value) => value.year === selectedYearOption);

		if(index === -1)
			return;
			
		const newValuesMonthlyData: string[][] = spreadsheetRef.current ? spreadsheetRef.current.getData() as string[][] : [];
		
		setValuesMonthlyData((prevState) => {
			const updatePrevState = prevState;
			updatePrevState[index].data = newValuesMonthlyData;
			return updatePrevState;
		});	

		// UPDATE TOTALS 

		const totalsIndex = totalsValuesMonthly.findIndex((value) => value.year === selectedYearOption);
		const value = stringToNumber(newValuesMonthlyData[12][1], currentLanguage);
		const realizedValue = stringToNumber(newValuesMonthlyData[12][2], currentLanguage);
		const releasedValue = stringToNumber(newValuesMonthlyData[12][3], currentLanguage);
		const valueRevenue = stringToNumber(newValuesMonthlyData[12][4], currentLanguage);
		const realizedRevenueValue = stringToNumber(newValuesMonthlyData[12][5], currentLanguage);
		

		if(totalsIndex === -1){

			setTotalsValuesMonthly((prevState) => {
				const updatePrevState = prevState;
				updatePrevState.push({
					year: selectedYearOption,
					value,
					realizedValue,
					releasedValue,
					valueRevenue, 
					realizedRevenueValue
				});
				return updatePrevState;
			});
			setValueAddOrChange("inb001Valor", value + stringToNumber(watchAddOrChange("inb001Valor"), currentLanguage));
			setValueAddOrChange("inb001Vlrrealizado", realizedValue + stringToNumber(watchAddOrChange("inb001Vlrrealizado"), currentLanguage));
			setValueAddOrChange("inb001Vlrliberado", releasedValue + stringToNumber(watchAddOrChange("inb001Vlrliberado"), currentLanguage));
			setValueAddOrChange("inb001Vlrprevreceita", valueRevenue + stringToNumber(watchAddOrChange("inb001Vlrprevreceita"), currentLanguage));
			setValueAddOrChange("inb001Vlrrealreceita", realizedRevenueValue + stringToNumber(watchAddOrChange("inb001Vlrrealreceita"), currentLanguage));


			return;
		}

		if(totalsValuesMonthly[totalsIndex].value !== value)
			setValueAddOrChange("inb001Valor", (value - totalsValuesMonthly[totalsIndex].value) + stringToNumber(watchAddOrChange("inb001Valor"), currentLanguage));
		if(totalsValuesMonthly[totalsIndex].realizedValue !== realizedValue)
			setValueAddOrChange("inb001Vlrrealizado", (realizedValue - totalsValuesMonthly[totalsIndex].realizedValue) + stringToNumber(watchAddOrChange("inb001Vlrrealizado"),currentLanguage));
		if(totalsValuesMonthly[totalsIndex].releasedValue !== releasedValue)
			setValueAddOrChange("inb001Vlrliberado", (releasedValue - totalsValuesMonthly[totalsIndex].releasedValue) + stringToNumber(watchAddOrChange("inb001Vlrliberado"),currentLanguage));
		if(totalsValuesMonthly[totalsIndex].valueRevenue !== valueRevenue)
			setValueAddOrChange("inb001Vlrprevreceita", (valueRevenue - totalsValuesMonthly[totalsIndex].valueRevenue) + stringToNumber(watchAddOrChange("inb001Vlrprevreceita"),currentLanguage));
		if(totalsValuesMonthly[totalsIndex].realizedRevenueValue !== realizedRevenueValue)
			setValueAddOrChange("inb001Vlrrealreceita", (realizedRevenueValue - totalsValuesMonthly[totalsIndex].realizedRevenueValue) + stringToNumber(watchAddOrChange("inb001Vlrrealreceita"),currentLanguage));

		
		setTotalsValuesMonthly((prevState) => {
			const updatePrevState = prevState;
			updatePrevState[totalsIndex].value = value;
			updatePrevState[totalsIndex].realizedValue = realizedValue;
			updatePrevState[totalsIndex].releasedValue = releasedValue;
			updatePrevState[totalsIndex].valueRevenue = valueRevenue;
			updatePrevState[totalsIndex].realizedRevenueValue = realizedRevenueValue;
			return updatePrevState;
		});


	};

	const callbackComments = async (id: number) => {
		if(!id)
			return;

		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Actions/CommentsNumber",
				urlParameters: {
					id
				}
			});	

			setSelectedAction({...selectedAction, commentsNumber: data});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar número de comentários, favor atualizar a pagina"), addToast);
		}
	};

	const callbackAttachments = async (id: number) => {
		if(!id)
			return;

		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Actions/AttachmentsNumber",
				urlParameters: {
					id
				}
			});	

			setSelectedAction({...selectedAction, attachmentsNumber: data});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar número de anexos, favor atualizar a pagina"), addToast);
		}
	};

	const iconModal = () => {
		if(isAdd) return "plus";
		if(isCopy) return "copy";
		return "pencil";
	};

	useImperativeHandle(ref, () => ({
		actionButtonDelete,
		openModalAddOrChange
	}));

	return (
		<>		
			{/* Modal ADD/CHANGE */}
			<Modal
				type={isCopy ? ITypeModal.COPY : undefined}
				ref={modalAddOrChange}
				header={{
					title,
					subtitle,
					icon: {
						name: iconModal()
					}
				}}
				footer={{
					showSaveButton: !isDisabled,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: isCopy ? translate("Copiar") : translate("Salvar"),
					cancelButtonText: translate("Cancelar"),
					childrenOfStandardFooter: <>
						{isAdd && (
							<RCCButton disabled={modalAddOrChange.current?.buttonLoading || false} color="blue" size="sm" onClick={() => {
								const inb001Titulo = watchAddOrChange("inb001Titulo");
								let inb001Efa005Id = watchAddOrChange("inb001Efa005Id");

								if(typeof inb001Efa005Id === "object")
									inb001Efa005Id = inb001Efa005Id.value;

								let inb001Efa005Id2 = watchAddOrChange("inb001Efa005Id2");

								if(typeof inb001Efa005Id2 === "object")
									inb001Efa005Id2 = inb001Efa005Id2.value;

								const inb001Taa001Id = watchAddOrChange("inb001Taa001Id");
								const inb001Onde = watchAddOrChange("inb001Onde");
								const inb001Motivo = watchAddOrChange("inb001Motivo");
								const inb001Como = watchAddOrChange("inb001Como");
								const inb001Dtinicio = watchAddOrChange("inb001Dtinicio");
								const inb001Dtfim = watchAddOrChange("inb001Dtfim");
								const inb001Tipo = watchAddOrChange("inb001Tipo");

								setSelectedAction((prevState) => ({
									...prevState,
									inb001Efa005Id,
									inb001Efa005Id2,
									inb001Taa001Id,
									inb001Onde,
									inb001Motivo,
									inb001Como,
									inb001Dtinicio,
									inb001Dtfim,
									inb001Titulo, 
									inb001Tipo
								}));
								setFormSimplified((prevState) => !prevState);
							}}>
								{formSimplified ? translate("Ver cadastro completo") : translate("Ver cadastro simplificado")}
							</RCCButton>
						)}
					</>
				}}
				onSubmit={handleSubmitAddOrChange(submitConfirm)}
				onClose={() => {
					if(!isAdd && onCallbackAddOrChange.current !== undefined)
						onCallbackAddOrChange.current();
				}}
				withoutForm
			>
				{formSimplified ? (
					<RCCGrid
						container
						item
						direction="column"
						alignItems="center"
					>
						<FormSimplifiedFields 
							setValueAddOrChange={setValueAddOrChange}
							controlAddOrChange={controlAddOrChange}
							errorsAddOrChange={errorsAddOrChange}
							isAdd={isAdd}
							isCopy={isCopy}
							isDisabled={isDisabled}
							registerAddOrChange={registerAddOrChange}
							selectedAction={selectedAction}
							modalShowPostponements={modalShowPostponements}
						/>
						
						<RCCGrid
							container
							item
							xs={12}
							sm={10}
							md={8}
							lg={6}
							alignItems="flex-end"
						>
							<RCCGrid
								container
								item
								xs={12}
								sm={6}
							>
								<LovUsers 
									control={controlAddOrChange}
									name="inb001Efa005Id"
									label={translate("Responsável")}
									defaultValue={selectedAction.inb001Efa005Id ? selectedAction.inb001Efa005Id?.toString() : {
										value: user.efa005Id,
										label:  user.efa005Nome 
									}}
									error={errorsAddOrChange.inb001Efa005Id}
									required
									disabled={isDisabled}
								/>
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={12}
								sm={5}
							>
								<RCCCheckBox 
									name="inb001Notificaresponsavel"
									defaultChecked={selectedAction.inb001Notificaresponsavel !== "N"}
									label={translate("Acompanhar andamento")}
									labelPosition="end"
									register={registerAddOrChange}
									styleInline={{checkbox: "padding-left: 5%;"}}
									error={errorsAddOrChange.inb001Notificaresponsavel}
									disabled={isDisabled}
								/>
							</RCCGrid>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
							sm={10}
							md={8}
							lg={6}
							alignItems="flex-end"
						>
							<RCCGrid
								container
								item
								xs={12}
								sm={6}
							>
								<LovUsers 
									control={controlAddOrChange}
									name="inb001Efa005Id2"
									label={translate("Autoridade")}
									defaultValue={selectedAction.inb001Efa005Id2 ? selectedAction.inb001Efa005Id2?.toString() : {
										value: user.efa005Id ,
										label:  user.efa005Nome 
									}}
									error={errorsAddOrChange.inb001Efa005Id2}
									required
									disabled={isDisabled}
								/>
							</RCCGrid>
							<RCCGrid
								container
								item
								xs={12}
								sm={5}
							>
								<RCCCheckBox 
									name="inb001Notificaautoridade"
									defaultChecked={selectedAction.inb001Notificaautoridade !== "N"}
									label={translate("Acompanhar andamento")}
									labelPosition="end"
									register={registerAddOrChange}
									styleInline={{checkbox: "padding-left: 5%;"}}
									error={errorsAddOrChange.inb001Notificaautoridade}
									disabled={isDisabled}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				): (
					<Tabs 
						name="TabActions"
						tabInit={tabInit}
						options={[{
							label: translate("Geral"),
							content: (
								<RCCGrid
									container
									item
									direction="column"
									alignItems="center"
								>
									{!isAdd && (
										<>
											<RCCGrid
												container
												item
												xs={12}
												sm={10}
												md={8}
												lg={6}
												justifyContent="flex-end"
											>
												<Title color="purple">
													{translate("Código")}: {selectedAction.inb001Codigo}
												</Title>
	
												{selectedAction.inb001Dtconclusao ? (
													<Title color="purple">
														{translate("Data de conclusão")}:
														{formatDate(selectedAction.inb001Dtconclusao, currentLanguage)}
														<RCCIcon name="calendar" style={{ marginLeft: "0.2rem"}}/>
													</Title>
												): (
													<Title color="purple">
														{translate("Não concluída")}
														<RCCIcon name="infoCircle" style={{ marginLeft: "0.2rem"}} />
													</Title>
												)}
												{selectedAction.traceability && Object.keys(selectedAction.traceability).length > 0 && (
													<BadgeTraceabilityBasic 
														layout="modal"
														traceability={selectedAction.traceability}
													/>
												)}
												<RCCTooltip color="grey" text={translate("Histórico")}>
													<RCCButton color="grey" icon={{ name: "history", size: 13 }}
														onClick={() => {
															if(!modalHistoryRef.current)
																return;
															modalHistoryRef.current.openModalActionHistory(selectedAction.inb001Id, `${selectedAction.inb001Codigo} - ${selectedAction.inb001Titulo}`, false, () => callbackAttachments(selectedAction.inb001Id), isDisabled);
														}}
													/>
												</RCCTooltip>
												<RCCTooltip color="grey" text={translate("Comentários")}>
													<RCCButton color="grey" icon={{ name: selectedAction.commentsNumber > 0 ? "commentFilled" : "comment", size: 12 }}
														onClick={() => {
															if(!modalCommentsRef.current)
																return;
															modalCommentsRef.current.openModalActionComments(selectedAction.inb001Id, `${selectedAction.inb001Codigo} - ${selectedAction.inb001Titulo}`, () => callbackComments(selectedAction.inb001Id) );
														}}
													>
														{selectedAction.commentsNumber ? selectedAction.commentsNumber.toString() : "0"}
													</RCCButton>
												</RCCTooltip>
												<RCCTooltip color="grey" text={translate("Anexos")}>
													<RCCButton color="grey" icon={{ name: "attachment", size: 12 }}
														onClick={() => {
															if(!modalHistoryRef.current)
																return;
															modalHistoryRef.current.openModalActionHistory(selectedAction.inb001Id, `${selectedAction.inb001Codigo} - ${selectedAction.inb001Titulo}`, true, () => callbackAttachments(selectedAction.inb001Id), isDisabled);
														}}
													>
														{selectedAction.attachmentsNumber ? selectedAction.attachmentsNumber.toString() : "0"}
													</RCCButton>
												</RCCTooltip>
												<RCCGrid item>
													<Audit data={selectedAction} type="pop" />
												</RCCGrid>
											</RCCGrid>
										</>
									)}
									
									<FormSimplifiedFields 
										setValueAddOrChange={setValueAddOrChange}
										controlAddOrChange={controlAddOrChange}
										errorsAddOrChange={errorsAddOrChange}
										isAdd={isAdd}
										isCopy={isCopy}
										isDisabled={isDisabled}
										registerAddOrChange={registerAddOrChange}
										selectedAction={selectedAction}
										modalShowPostponements={modalShowPostponements}
									/>
						
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										md={8}
										lg={6}
									>
										<RCCGrid
											container
											item
											xs={12}
											sm={6}
										>
											<RCCSelectOptionsCard
												control={controlAddOrChange}
												name="inb001Urgencia"
												label="Urgente"
												defaultValue={Number.isNaN(Number(selectedAction.inb001Urgencia)) ? "null" : String(selectedAction.inb001Urgencia)}
												options={[{
													label: translate("Sim"),
													value: "10"
												},{
													label: translate("Não"),
													value: "0"
												},{
													label: translate("Indefinido"),
													value: "null"
												}]}
												disabled={isDisabled}
											/>
										</RCCGrid>
										<RCCGrid
											container
											item
											xs={12}
											sm={6}
										>
											<RCCSelectOptionsCard
												control={controlAddOrChange}
												name="inb001Importancia"
												label="Importante"
												defaultValue={Number.isNaN(Number(selectedAction.inb001Importancia)) ? "null" : String(selectedAction.inb001Importancia)}
												options={[{
													label: translate("Sim"),
													value: "10"
												},{
													label: translate("Não"),
													value: "0"
												},{
													label: translate("Indefinido"),
													value: "null"
												}]}
												disabled={isDisabled}
											/>
										</RCCGrid>
									</RCCGrid>
									<RCCGrid
										container
										item
										xs={12}
										sm={10}
										md={8}
										lg={6}
									>
										<LovTags 
											control={controlAddOrChange}
											name="inb001Tags"
											label={translate("Tags")}
											defaultValue={Array.isArray(selectedAction.inb001Tags) ? selectedAction.inb001Tags.join(", ") : selectedAction.inb001Tags}
											disabled={isDisabled}
											extraParametersOnLoad={{
												use: 3
											}}
											extraParametersOnCreate={{
												use: 3
											}}
										/>
									</RCCGrid>
								</RCCGrid>
							)
						},{
							label: translate("RACI"),
							content: (
								<RACI 
									accountable={{
										nameOfSelect: "inb001Efa005Id2",
										nameOfCheckbox: "inb001Notificaautoridade",
										defaultValue: {
											select: selectedAction.inb001Efa005Id2 ? selectedAction.inb001Efa005Id2.toString() : undefined,
											checkbox: selectedAction.inb001Notificaautoridade
										}
									}}
									control={controlAddOrChange}
									register={registerAddOrChange}
									unregister={unregisterAddOrChange}
									clearErrors={clearErrorsAddOrChange}
									error={errorsAddOrChange}
									disabled={isDisabled}
									participants={{
										nameOfObject: "intbb002",
										nameOfID: "inb002Id",
										nameOfCheckbox: "inb002Notificaparticipante",
										nameOfSelectGroup: "inb002Efa004Id",
										nameOfSelectGroupDescription: "inb002Efa004Nome",
										nameOfSelectUser: "inb002Efa005Id",
										nameOfSelectUserDescription: "inb002Efa005Nome",
										nameOfType: "inb002Tipo",
										defaultValue: selectedAction.intbb002
									}}
									responsible={{
										nameOfSelect: "inb001Efa005Id",
										nameOfCheckbox: "inb001Notificaresponsavel",
										defaultValue: {
											select: selectedAction.inb001Efa005Id ? selectedAction.inb001Efa005Id.toString() : undefined,
											checkbox: selectedAction.inb001Notificaresponsavel
										}
									}}
								/>
							)
						},{
							label: translate("Valores"),
							content: (
								<>
									<Title color="grey" size="xs"> {translate("Defina a configuração dos valores da ação. Ela pode ser definida diretamente com seus valores totais ou mês a mês")}. </Title>
	
									<RCCGrid container item>
										<RCCSelectOptionsCard
											name="inb001Informavalores" 
											label={translate("Informar valores")} 
											options={optionsInformValues}
											onChange={(value: string) => onChangeInformValues(value, optionsMonthly[1]?.value || new Date(selectedAction.inb001Dtinicio).getFullYear().toString())} 
											control={controlAddOrChange} 
											defaultValue={selectedAction.inb001Informavalores && selectedAction.inb001Informavalores > 0 ? selectedAction.inb001Informavalores.toString() : ActionInformValues.TOTAL}
											disabled={isDisabled}
										/>
									</RCCGrid>
									
									<hr />
									<Title color="purple" marginBottom={15}> {translate("Totais")} </Title>
	
									<RCCGrid container alignItems="center" direction="column">
	
										<RCCGrid container item spacing={1} justifyContent="space-between">
											<RCCGrid item xs={12} sm={2}>
												<InputNumber 
													name="inb001Valor" 
													control={controlAddOrChange}
													label={`${translate("Previsto")} (${translate("despesa")})`} 
													defaultValue={selectedAction.inb001Valor || 0}
													disabled={showValuesMonthly || isDisabled}	
													maxLength={15}
												/>
											</RCCGrid>
	
											<RCCGrid item xs={12} sm={2}>
												<InputNumber 
													name="inb001Vlrrealizado" 
													control={controlAddOrChange}
													label={`${translate("Realizado")} (${translate("despesa")})`} 
													defaultValue={selectedAction.inb001Vlrrealizado || 0}
													disabled={showValuesMonthly || isDisabled}	
													maxLength={15}
												/>
											</RCCGrid>
	
											<RCCGrid item xs={12} sm={2}>
												<InputNumber 
													name="inb001Vlrliberado" 
													label={translate("Liberado")}
													control={controlAddOrChange} 
													defaultValue={selectedAction.inb001Vlrliberado || 0}
													disabled={showValuesMonthly || isDisabled}
													maxLength={15}
												/>
											</RCCGrid>
	
											<RCCGrid item xs={12} sm={2}>
												<InputNumber 
													name="inb001Vlrprevreceita" 
													label={`${translate("Previsto")} (${translate("receita")})`} 
													control={controlAddOrChange}
													defaultValue={selectedAction.inb001Vlrprevreceita || 0} 
													disabled={showValuesMonthly || isDisabled}
													maxLength={15}
												/>
											</RCCGrid>
	
											<RCCGrid item xs={12} sm={2}>
												<InputNumber 
													name="inb001Vlrrealreceita" 
													label={`${translate("Realizado")} (${translate("receita")})`} 
													control={controlAddOrChange}
													defaultValue={selectedAction.inb001Vlrrealreceita || 0} 
													disabled={showValuesMonthly || isDisabled}
													maxLength={15}
												/>
											</RCCGrid>
	
										</RCCGrid>									
	
									</RCCGrid>
	
									{showValuesMonthly && (
										<>
											<hr />
	
											<Title color="purple" marginBottom={15}> {translate("Mensal")} </Title>
											<RCCGrid container item>
												<RCCGrid item xs={4} sm={2} lg={1}>
													<Select 
														control={controlAddOrChange}
														name="periodsMonthly"
														options={optionsMonthly}
														defaultValue={optionsMonthly[1]?.value || new Date(selectedAction.inb001Dtinicio).getFullYear().toString()}
														onChangeCapture={onChangeSelectPeriodsValuesMonthly}
														disabled={isDisabled}
														styleInline="margin-top: -18px;"
													/>
												</RCCGrid>
												<Spreadsheet 
													ref={spreadsheetRef}
													data={spreadsheetData}
													editable={!isDisabled}
													columns={[
														{ type: "text", title: translate("Mês"), width: 30, readOnly: true, wordWrap: true },
														{ type: "numeric", title: translate("Previsto despesa"), width: 130,  decimal: "," },
														{ type: "numeric", title: translate("Realizado despesa"), width: 130, decimal: ","},
														{ type: "numeric", title: translate("Liberado"), width: 130, decimal: ","},
														{ type: "numeric", title: translate("Previsto receita"), width: 130, decimal: ","},
														{ type: "numeric", title: translate("Realizado receita"), width: 130, decimal: ","}
													]}
													readonlyLastRows={1}
													format={{
														columns: [1, 2, 3, 4, 5]
													}}
													showButtons={false}
													onChange={onChangeSpreadsheet}
												/>
	
											</RCCGrid>
											
	
										</>
									)}
									
								</>
							)
						},{
							label: "Checklists",
							content: (
								<Checklists
									subTitle={!isAdd && !actionNotStartedOrDraft ? translate("Ao alterar os checklist, os percentuais do andamento da ação e seus respectivos históricos serão recalculados") : ""} 
									checklistsDefaultValue={selectedAction.intbb009 ? selectedAction.intbb009  : []}
									clearErrors={clearErrorsAddOrChange}
									disabled={isDisabled}
									error={errorsAddOrChange}
									register={registerAddOrChange}
									control={controlAddOrChange}
									unregister={unregisterAddOrChange}
									names={{
										nameOfObject: "intbb009",
										nameOfID: "inb009Id",
										nameOfTitle: "inb009Titulo",
										nameOfItems: "intbb010",
										nameOfItemID: "inb010Id",
										nameOfItemDesc: "inb010Item",
										nameOfItemVerified: "inb010Verificado",
										nameOfRangeSlider: "inb009Peso",
										nameOfItemRangeSlider: "inb010Peso"
									}}
								/>
								
							)
						},{
							label: translate("Recorrência"),
							content: (
								<>
									<RCCGrid container item>
										<RCCSwitch 
											name="generateRecurrence" 
											label={translate("Gerar recorrência")}
											onChangeCapture={(e: ChangeEvent<HTMLInputElement>) => setGenerateRecurrence(e.target.checked)}
										/>
									</RCCGrid>
									{generateRecurrence && (
										<>
											<RCCGrid container item>
												<RCCSelectOptionsCard
													control={controlAddOrChange}
													name="recurrenceType"
													label=" "
													defaultValue={RecurrenceType.MONTHLY}
													options={[{
														label: translate("Diário"),
														value: RecurrenceType.DAILY
													},{
														label: translate("Semanal"),
														value: RecurrenceType.WEEKLY
													},{
														label: translate("Mensal"),
														value: RecurrenceType.MONTHLY
													},{
														label: translate("Anual"),
														value: RecurrenceType.ANNUAL
													}]}
													onChange={(e) => {
														switch(e){
														case RecurrenceType.DAILY:
															setLabelRecurrencePeriod("Dias");
															break;
														case RecurrenceType.WEEKLY:
															setLabelRecurrencePeriod("Semanas");
															break;
														case RecurrenceType.MONTHLY:
															setLabelRecurrencePeriod("Meses");
															break;
														case RecurrenceType.ANNUAL:
															setLabelRecurrencePeriod("Anos");
															break;
														default:
															break;
														}
													}}
												/>		
											</RCCGrid>
	
	
											<RCCGrid container item xs={12} sm={4} md={3} lg={2}>
												<RCCInput 
													register={registerAddOrChange}
													name="recurrencePeriod"
													label={`${translate("a cada")} ${labelNumberRecurrencePeriod || "1"} ${translate(labelRecurrencePeriod)}`}
													defaultValue={1}
													type="number"
													min={1}
													onChangeCapture={(e:any) => {setLabelNumberRecurrencePeriod(e.target.value);}}
												/>		
											</RCCGrid>
	
											<RCCGrid container item xs={12} sm={4} md={3} lg={2}>
												<DateTimePicker 
													type="datePicker"
													control={controlAddOrChange}
													name="recurrenceEndDate"
													label={translate("Data final")}
													defaultValue={new Date().setMonth(new Date().getMonth() + 1)}
												/>		
											</RCCGrid>
										</>
									)}
								</>
							),
							hidden: !isAdd || formSimplified
						},{
							label: translate("Eficácia"),
							content: (
								<>
									<RCCGrid container item alignItems="center" >
										<RCCSwitch 
											name="inb001Exigeeficacia" 
											register={registerAddOrChange}
											label={translate("Exige eficácia")}
											defaultChecked ={selectedAction.inb001Exigeeficacia === "S"}
											onChangeCapture={(e: ChangeEvent<HTMLInputElement>) => setRequireEffectiveness(e.target.checked)}
											disabled={isDisabled}
										/>
										{requireEffectiveness && !isAdd && (
											<>
												<RCCTooltip color="grey" text={translate("Eficácia")} >
													<RCCButton color="transparent" icon={{
														name:"listCheck", 
														size: 32,
														color: "var(--grey)"
													}}
													onClick={() => {
														if(!modalEfficiencyRef.current)
															return;
														modalEfficiencyRef.current.openModalActionEfficiency(selectedAction.inb001Id, `${selectedAction.inb001Codigo} - ${selectedAction.inb001Titulo}`, undefined);
													}}
													/>
												</RCCTooltip>
											</>
										)}
									</RCCGrid>
									{requireEffectiveness && (
										<>
											<RCCGrid container item spacing={1}>
												<RCCGrid
													container
													item
													xs={12}
													sm={8}
													md={6}
													lg={4}
												>
													<LovUsersGroups 
														control={controlAddOrChange}
														label={translate("Grupo de qualidade")}
														name="inb001Efa004Id"
														defaultValue={selectedAction.inb001Efa004Id && (selectedAction.inb001Efa004Nome ? { value: String(selectedAction.inb001Efa004Id), label: selectedAction.inb001Efa004Nome } : selectedAction.inb001Efa004Id?.toString())}
														isClearable
														required
														error={errorsAddOrChange.inb001Efa004Id}
														disabled={isDisabled}
													/>
												</RCCGrid>
												<RCCGrid
													container
													item
													xs={11}
													sm={2}
												>
													<RCCInput 
														register={registerAddOrChange}
														label={translate("Dias")}
														name="inb001Diaseficacia"
														defaultValue={selectedAction.inb001Diaseficacia || 7}
														disabled={isDisabled}
														type="number"
														maxLength={3}
														min={1}
													/>
												</RCCGrid>
												<RCCGrid
													container
													item
													xs={11}
													sm={1}
													alignItems="flex-end"
												>
													<RCCTooltip 
														 color="grey" 
														 text={translate("Defina o número de dias que será utilizado para alertar no painel se a eficácia está ou não atrasada")}
														styleInline="width: 220px;"
													>
														<RCCIcon 
															name="info"
															color="var(--grey)"
															style={{marginBottom: "8px"}}
														/>
													</RCCTooltip> 
												</RCCGrid>
											</RCCGrid>
										</>
									)}
								</>
							)
						}]}
					/>
				)}
			</Modal>
		
			{/* Modal Comment used inside ADD/Change */}
			<CommentsModal ref={modalCommentsRef} />

			{/* Modal History used inside ADD/Change */}
			<ModalHistory ref={modalHistoryRef} />

			{/* Modal Efficiency used inside ADD/Change */}
			<ModalEfficiency ref={modalEfficiencyRef} />

			<ModalShowPostponements ref={modalShowPostponements} />
		</>
	);
};

export default forwardRef(ModalAddChangeDelete);
